/**
 * The BIM viewer's frame contract (card t_d8972178).
 *
 * web-ifc opens every model with `COORDINATE_TO_ORIGIN` (`useBimScene.ts`), which
 * translates all geometry by the model's own "coordination" translation. That is
 * what keeps the float32 vertex buffers small — a georeferenced IFC can sit
 * hundreds of kilometres from its origin — but it also means the rendered
 * coordinates are NOT IFC coordinates any more.
 *
 * Everything the hub stores for a model speaks plain IFC metres: the device-icon
 * widgets (`otto-design/DemoHouse/house-ifc/tools/widgets.py`: "(x, y, z)_ifc ->
 * (x, z, -y)_viewer … the viewer's world coordinates are plain IFC coordinates
 * in metres"), the in-app element editor's placements and the saved viewpoints.
 * Measured on the demo house (2026-10-10, `TorikDemoHouse-assembled-site.ifc`,
 * `scripts/hermes-bim-frame-probe.cjs`): the flag moved the model by
 * [1198.146, 1.26, −17.853] m, so all 69 widgets — authored in the un-moved
 * frame — rendered 1198 m away from the house, visible only far off screen.
 *
 * The rule, therefore: **the scene's world coordinates ARE IFC coordinates.**
 * The loader reads web-ifc's coordination matrix and undoes its translation on
 * the model GROUP (a float64 matrix), never on the vertices — the geometry keeps
 * the small float32 values AND a stored widget point is a world point. There is
 * no second frame to keep in sync: no conversion on read, none on write.
 */

export interface IfcFrameOffset {
  x: number;
  y: number;
  z: number;
}

/**
 * The model-group position that puts the scene in the IFC frame: the inverse of
 * the translation inside web-ifc's coordination matrix.
 *
 * The matrix is column-major (16 numbers), so its translation sits in entries
 * 12..14. Returns `null` when the matrix is missing, short or not finite — the
 * caller then leaves the model where web-ifc put it (a model still renders, it
 * simply renders in web-ifc's frame) instead of teleporting it by a bad number.
 */
export function ifcFrameGroupOffset(coordinationMatrix: ArrayLike<number> | null | undefined): IfcFrameOffset | null {
  if (!coordinationMatrix || coordinationMatrix.length < 16) return null;
  const x = -coordinationMatrix[12];
  const y = -coordinationMatrix[13];
  const z = -coordinationMatrix[14];
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  return { x, y, z };
}

/**
 * How far outside the model a stored point may sit and still belong to its frame:
 * 10× the model's own diagonal. A viewpoint saved before the frame rule landed is
 * off by the model's whole georeference (~1198 m for a 30 m house ⇒ ~40×), while
 * a deliberately far overview stays well inside it.
 */
export const MODEL_FRAME_MARGIN = 10;

/**
 * Whether a stored point (a viewpoint's camera target) still lies in the model's
 * frame. `centre` and `diagonal` are the model's own world box, which the caller
 * reads from the loaded meshes; an empty model admits everything.
 */
export function viewpointInModelFrame(
  target: { x: number; y: number; z: number },
  centre: { x: number; y: number; z: number },
  diagonal: number,
  margin: number = MODEL_FRAME_MARGIN,
): boolean {
  const dx = target.x - centre.x;
  const dy = target.y - centre.y;
  const dz = target.z - centre.z;
  const reach = margin * Math.max(diagonal, 1);
  return dx * dx + dy * dy + dz * dz <= reach * reach;
}
